import { api } from "../api";
import { iconoLapiz, iconoMas } from "../iconos";
import type { Cliente, Lista } from "../../lib/types";

type ClienteFila = Cliente & { lista_nombre: string };

function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] as string,
  );
}

export async function renderClientes(contenedor: HTMLElement): Promise<void> {
  let listas: Lista[] = [];
  let todos: ClienteFila[] = [];

  try {
    const meta = await api.get<{ listas: Lista[] }>("/api/meta");
    listas = meta.listas;
  } catch {
    /* ignore */
  }
  const opcLista = (sel?: number) =>
    listas
      .map(
        (l) => `<option value="${l.id}"${l.id === sel ? " selected" : ""}>${esc(l.nombre)}${l.descripcion ? " — " + esc(l.descripcion) : ""}</option>`,
      )
      .join("");

  contenedor.innerHTML = `
    <div class="mb-4">
      <h1 class="titulo-pagina">Clientes</h1>
      <p class="subtitulo">Alta y edicion de clientes mayoristas.</p>
    </div>

    <details class="tarjeta mb-4">
      <summary class="flex cursor-pointer list-none items-center gap-2 text-sm font-bold uppercase tracking-wide text-masa-600">
        <span class="text-masa-500">${iconoMas}</span> Nuevo cliente
      </summary>
      <form id="f" class="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label><span class="etiqueta">Nombre</span><input id="nombre" class="campo" placeholder="Nombre del comercio" /></label>
        <label><span class="etiqueta">Lista de precios</span><select id="lista_id" class="campo">${opcLista()}</select></label>
        <label><span class="etiqueta">Contacto</span><input id="contacto" class="campo" placeholder="Persona de contacto" /></label>
        <label><span class="etiqueta">Direccion</span><input id="direccion" class="campo" /></label>
        <label><span class="etiqueta">Telefono</span><input id="telefono" class="campo" /></label>
        <div class="flex items-center gap-2 sm:col-span-2">
          <button class="btn-primario">${iconoMas} Agregar</button>
          <span id="err" class="text-sm text-red-600"></span>
        </div>
      </form>
    </details>

    <div class="tarjeta overflow-x-auto p-0">
      <table class="tabla">
        <thead><tr>
          <th>Nombre</th><th>Lista</th><th>Contacto</th><th>Telefono</th><th class="text-right">Acciones</th>
        </tr></thead>
        <tbody id="tbody"></tbody>
      </table>
      <div id="pie" class="border-t border-crema-200 px-3 py-2 text-sm text-masa-500"></div>
    </div>

    <dialog id="dlg"></dialog>`;

  const tbody = contenedor.querySelector("#tbody") as HTMLElement;
  const pie = contenedor.querySelector("#pie") as HTMLElement;
  const err = contenedor.querySelector("#err") as HTMLElement;
  const dlg = contenedor.querySelector("#dlg") as HTMLDialogElement;

  function pintar(): void {
    tbody.innerHTML = todos
      .map(
        (c) => `<tr>
          <td class="font-medium">${esc(c.nombre)}</td>
          <td>${esc(c.lista_nombre)}</td>
          <td>${esc(c.contacto ?? "")}</td>
          <td>${esc(c.telefono ?? c.celular ?? "")}</td>
          <td class="text-right"><button class="chip-accion" data-editar="${c.id}" title="Editar">${iconoLapiz}</button></td>
        </tr>`,
      )
      .join("");
    pie.textContent = `Mostrando ${todos.length} clientes`;
  }

  async function cargar(): Promise<void> {
    tbody.innerHTML = `<tr><td colspan="5" class="py-6 text-center text-masa-400">Cargando...</td></tr>`;
    todos = await api.get<ClienteFila[]>("/api/clientes?activo=1");
    pintar();
  }

  const form = contenedor.querySelector("#f") as HTMLFormElement;
  form.addEventListener("submit", async (ev) => {
    ev.preventDefault();
    err.textContent = "";
    const val = (id: string) => (contenedor.querySelector("#" + id) as HTMLInputElement).value.trim();
    if (!val("nombre")) {
      err.textContent = "El nombre es obligatorio";
      return;
    }
    try {
      await api.post("/api/clientes", {
        nombre: val("nombre"),
        lista_id: Number((contenedor.querySelector("#lista_id") as HTMLSelectElement).value),
        contacto: val("contacto") || null, direccion: val("direccion") || null,
        telefono: val("telefono") || null,
      });
      form.reset();
      await cargar();
    } catch (e) {
      err.textContent = e instanceof Error ? e.message : "Error";
    }
  });

  tbody.addEventListener("click", (ev) => {
    const btn = (ev.target as HTMLElement).closest("[data-editar]");
    if (!btn) return;
    const c = todos.find((x) => x.id === Number(btn.getAttribute("data-editar")));
    if (c) abrirEdicion(c);
  });

  function abrirEdicion(c: ClienteFila): void {
    const campo = (id: string, lbl: string, valor: string | null, col = "") =>
      `<label class="${col}"><span class="etiqueta">${lbl}</span><input id="${id}" class="campo" value="${esc(valor ?? "")}" /></label>`;
    dlg.innerHTML = `
      <form method="dialog" class="tarjeta w-[30rem] max-w-full border-0 shadow-none">
        <h2 class="mb-1 text-lg font-bold">Editar cliente</h2>
        <div class="grid grid-cols-2 gap-3">
          ${campo("e_nombre", "Nombre", c.nombre, "col-span-2")}
          <label class="col-span-2"><span class="etiqueta">Lista de precios</span>
            <select id="e_lista" class="campo">${opcLista(c.lista_id)}</select></label>
          ${campo("e_contacto", "Contacto", c.contacto)}
          ${campo("e_direccion", "Direccion", c.direccion)}
          ${campo("e_telefono", "Telefono", c.telefono)}
          ${campo("e_celular", "Celular", c.celular)}
          ${campo("e_email", "Email", c.email, "col-span-2")}
          ${campo("e_notas", "Notas", c.notas, "col-span-2")}
          <label class="col-span-2 flex items-center gap-2 pt-1">
            <input id="e_activo" type="checkbox" ${c.activo ? "checked" : ""} class="h-4 w-4 accent-masa-600" />
            <span class="text-sm">Activo</span></label>
        </div>
        <p id="e_err" class="mt-2 text-sm text-red-600"></p>
        <div class="mt-4 flex justify-end gap-2">
          <button value="cancel" class="btn-sec">Cancelar</button>
          <button id="e_guardar" value="ok" class="btn-primario">Guardar</button>
        </div>
      </form>`;
    const eErr = dlg.querySelector("#e_err") as HTMLElement;
    dlg.querySelector("#e_guardar")?.addEventListener("click", async (ev) => {
      ev.preventDefault();
      eErr.textContent = "";
      const val = (id: string) => (dlg.querySelector("#" + id) as HTMLInputElement).value.trim();
      try {
        await api.put(`/api/clientes/${c.id}`, {
          nombre: val("e_nombre"),
          lista_id: Number((dlg.querySelector("#e_lista") as HTMLSelectElement).value),
          contacto: val("e_contacto") || null, direccion: val("e_direccion") || null,
          telefono: val("e_telefono") || null, celular: val("e_celular") || null,
          email: val("e_email") || null, notas: val("e_notas") || null,
          activo: (dlg.querySelector("#e_activo") as HTMLInputElement).checked ? 1 : 0,
        });
        dlg.close();
        await cargar();
      } catch (e) {
        eErr.textContent = e instanceof Error ? e.message : "Error";
      }
    });
    dlg.showModal();
  }

  await cargar();
}
